import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RISK_LEVELS, CONTROL_STATUSES, ACTION_STATUSES, ACTION_PRIORITIES } from './risk-components';

export const RiskFormDialog = ({ open, onClose, onSave, risk }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open) {
      setForm(risk ? {
        title: risk.title || '',
        description: risk.description || '',
        causes: risk.causes || '',
        consequences: risk.consequences || '',
        probability: risk.probability || 3,
        impact: risk.impact || 3,
        risk_level: risk.risk_level || 'midden',
        owner_name: risk.owner_name || risk.owner || '',
        initial_risk: risk.initial_risk || 0,
        current_risk: risk.current_risk || 0,
        target_risk: risk.target_risk || 0,
      } : {
        title: '', description: '', causes: '', consequences: '',
        probability: 3, impact: 3, risk_level: 'midden', owner_name: '',
        initial_risk: 9, current_risk: 9, target_risk: 4,
      });
    }
  }, [open, risk]);

  const handleSubmit = () => {
    if (!form.title?.trim()) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xl" data-testid="risk-form-dialog">
        <DialogHeader className="pb-3 border-b border-neutral-100">
          <DialogTitle className="text-base font-semibold">{risk?.id ? 'Risico bewerken' : 'Nieuw risico'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1 max-h-[60vh] overflow-y-auto">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Titel *</Label>
            <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="risk-form-title" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Uitleg</Label>
            <Textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="risk-form-description" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Oorzaken</Label>
              <Textarea value={form.causes || ''} onChange={e => setForm(f => ({ ...f, causes: e.target.value }))} rows={2} data-testid="risk-form-causes" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Consequenties</Label>
              <Textarea value={form.consequences || ''} onChange={e => setForm(f => ({ ...f, consequences: e.target.value }))} rows={2} data-testid="risk-form-consequences" />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 pt-3 border-t border-neutral-100">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Kans (1-5)</Label>
              <Select value={String(form.probability || 3)} onValueChange={v => setForm(f => ({ ...f, probability: parseInt(v) }))}>
                <SelectTrigger data-testid="risk-form-probability"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5].map(v => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Impact (1-5)</Label>
              <Select value={String(form.impact || 3)} onValueChange={v => setForm(f => ({ ...f, impact: parseInt(v) }))}>
                <SelectTrigger data-testid="risk-form-impact"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1,2,3,4,5].map(v => <SelectItem key={v} value={String(v)}>{v}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Risiconiveau</Label>
              <Select value={form.risk_level || 'midden'} onValueChange={v => setForm(f => ({ ...f, risk_level: v }))}>
                <SelectTrigger data-testid="risk-form-level"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RISK_LEVELS.map(l => <SelectItem key={l.key} value={l.key}>{l.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Eigenaar</Label>
            <Input value={form.owner_name || ''} onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} data-testid="risk-form-owner" />
          </div>
        </div>
        <DialogFooter className="pt-3 border-t border-neutral-100">
          <Button variant="outline" onClick={onClose} className="text-xs">Annuleren</Button>
          <Button onClick={handleSubmit} className="bg-black text-white text-xs" data-testid="risk-form-save">{risk?.id ? 'Opslaan' : 'Toevoegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ControlFormDialog = ({ open, onClose, onSave, control }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open && control) {
      setForm({
        id: control.id || '',
        title: control.title || '',
        description: control.description || '',
        status: control.status || 'Gedefinieerd',
      });
    }
  }, [open, control]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="control-form-dialog">
        <DialogHeader className="pb-3 border-b border-neutral-100">
          <DialogTitle className="text-base font-semibold">{form.id ? 'Control bewerken' : 'Nieuwe control'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Titel *</Label>
            <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="control-form-title" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Beschrijving</Label>
            <Textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={2} data-testid="control-form-description" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Status</Label>
            <Select value={form.status || 'Gedefinieerd'} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="control-form-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CONTROL_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter className="pt-3 border-t border-neutral-100">
          <Button variant="outline" onClick={onClose} className="text-xs">Annuleren</Button>
          <Button onClick={() => { if (form.title?.trim()) onSave(form); }} className="bg-black text-white text-xs" data-testid="control-form-save">{form.id ? 'Opslaan' : 'Toevoegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ActionFormDialog = ({ open, onClose, onSave, action }) => {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (open && action) {
      setForm({
        id: action.id || '',
        title: action.title || '',
        status: action.status || 'Open',
        priority: action.priority || 'Midden',
        owner: action.owner || '',
        date: action.date || new Date().toISOString().slice(0, 10),
      });
    }
  }, [open, action]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="action-form-dialog">
        <DialogHeader className="pb-3 border-b border-neutral-100">
          <DialogTitle className="text-base font-semibold">{form.id ? 'Actie bewerken' : 'Nieuwe actie'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-1">
          <div className="space-y-2">
            <Label className="text-xs font-medium text-neutral-600">Titel *</Label>
            <Input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="action-form-title" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Status</Label>
              <Select value={form.status || 'Open'} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="action-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Prioriteit</Label>
              <Select value={form.priority || 'Midden'} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="action-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ACTION_PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Eigenaar</Label>
              <Input value={form.owner || ''} onChange={e => setForm(f => ({ ...f, owner: e.target.value }))} data-testid="action-form-owner" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-medium text-neutral-600">Datum</Label>
              <Input type="date" value={form.date || ''} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} data-testid="action-form-date" />
            </div>
          </div>
        </div>
        <DialogFooter className="pt-3 border-t border-neutral-100">
          <Button variant="outline" onClick={onClose} className="text-xs">Annuleren</Button>
          <Button onClick={() => { if (form.title?.trim()) onSave(form); }} className="bg-black text-white text-xs" data-testid="action-form-save">{form.id ? 'Opslaan' : 'Toevoegen'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
